/**
 * Module dependencies.
 */

import { FilterType, Filters, FiltersListContext, Value } from './context';
import { ReactNode, useCallback, useMemo, useReducer } from 'react';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import qs from 'qs';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  omitUrl?: boolean;
};

/**
 * `Payload` type.
 */

type Payload = {
  filterType: FilterType;
  value: Value;
};

/**
 * `State` type.
 */

type State = {
  filters: Filters;
  submittedFilters: Filters;
};

/**
 * Action types.
 */

const actionTypes = {
  resetFilters: 'RESET_FILTERS',
  setFilter: 'SET_FILTER',
  submitClearFilters: 'SUBMIT_CLEAR_FILTERS',
  submitFilters: 'SUBMIT_FILTERS',
  submitRemoveFilters: 'SUBMIT_REMOVE_FILTERS'
};

/**
 * Normalize filter on add.
 */

function updateFilterOnSet(filters: Filters, payload: Payload): any {
  const filtersState = filters ?? {};
  const { filterType, value } = payload;

  return {
    ...filtersState,
    [filterType]: value
  };
}

/**
 * Normalize filter on remove.
 */

function normalizeFilterOnRemove(filters: Filters, payload: Payload) {
  const filtersState = filters ?? {};
  const { filterType, value } = payload;
  const newValue = filtersState[filterType]
    .split(',')
    .filter((key: string) => key !== value)
    .join(',');

  if (isEmpty(newValue)) {
    return omit(filtersState, [filterType]);
  }

  return {
    ...filtersState,
    [filterType]: newValue
  };
}

/**
 * Reducer.
 */

function reducer(state: State, { payload, type }) {
  switch (type) {
    case actionTypes.setFilter: {
      return {
        ...state,
        filters: updateFilterOnSet(state?.filters, payload)
      };
    }

    case actionTypes.resetFilters: {
      return {
        ...state,
        filters: state?.submittedFilters
      };
    }

    case actionTypes.submitClearFilters: {
      return {
        filters: {},
        submittedFilters: {}
      };
    }

    case actionTypes.submitFilters: {
      return {
        ...state,
        submittedFilters: state?.filters
      };
    }

    case actionTypes.submitRemoveFilters: {
      return {
        filters: normalizeFilterOnRemove(state?.filters, payload),
        submittedFilters: normalizeFilterOnRemove(
          state?.submittedFilters,
          payload
        )
      };
    }

    default:
      return state;
  }
}

/**
 * `useInitialState` hook.
 */

function useInitialState(omitUrl: boolean): State {
  const router = useRouter();
  const query = omitUrl ? null : qs.parse(Object(router.query));

  return {
    filters: query?.filter ?? null,
    submittedFilters: query?.filter ?? null
  };
}

/**
 * `FiltersListProvider` provider.
 */

const FiltersListProvider = ({ children, omitUrl }: Props) => {
  const initialState = useInitialState(omitUrl);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { filters, submittedFilters } = state;

  // On set value for filter.
  const handleSet = useCallback(
    (filterType: FilterType, value: Value) => {
      const dispatchedAction = {
        payload: { filterType, value },
        type: actionTypes.setFilter
      };

      dispatch(dispatchedAction);

      return reducer(state, dispatchedAction);
    },
    [state]
  );

  // On submit filters.
  const handleSubmit = useCallback(() => {
    const dispatchedAction = {
      payload: null,
      type: actionTypes.submitFilters
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On submit clear filters.
  const handleSubmitClear = useCallback(() => {
    const dispatchedAction = {
      payload: null,
      type: actionTypes.submitClearFilters
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On submit remove value from filter.
  const handleSubmitRemove = useCallback(
    (filterType: FilterType, value: Value) => {
      const dispatchedAction = {
        payload: { filterType, value },
        type: actionTypes.submitRemoveFilters
      };

      dispatch(dispatchedAction);

      return reducer(state, dispatchedAction);
    },
    [state]
  );

  // On reset filters.
  const handleReset = useCallback(() => {
    const dispatchedAction = {
      payload: null,
      type: actionTypes.resetFilters
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // Check if value is selected.
  const handleIsSelected = useCallback(
    (filterType: FilterType, value: string) => {
      const filtersState = filters ?? {};
      const hasFilterType = Object.keys(filtersState).includes(filterType);

      return (
        hasFilterType && isEqual(filtersState[filterType], JSON.parse(value))
      );
    },
    [filters]
  );

  const normalizedSelectedFilters = useMemo(() => {
    return isEmpty(filters) ? null : filters;
  }, [filters]);

  const normalizedSubmittedFilters = useMemo(() => {
    return isEmpty(submittedFilters) ? null : submittedFilters;
  }, [submittedFilters]);

  return (
    <FiltersListContext.Provider
      value={{
        filters: normalizedSelectedFilters,
        isFilterSelected: handleIsSelected,
        omitUrl,
        onSet: handleSet,
        onSubmit: handleSubmit,
        onSubmitClear: handleSubmitClear,
        onSubmitRemove: handleSubmitRemove,
        reset: handleReset,
        submittedFilters: normalizedSubmittedFilters
      }}
    >
      {children}
    </FiltersListContext.Provider>
  );
};

/**
 * Export `FiltersListProvider` provider.
 */

export default FiltersListProvider;
